import storageFallback from 'local-storage-fallback';
import { isXamanXappEnvironment } from '@/xrpl/xaman-env';

const getBrowserStorage = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  return storageFallback;
};

/** localStorage backend: sync under the hood, async API for one call shape. */
export const localStorageBackend = {
  async get(key) {
    try {
      const item = getBrowserStorage().getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },

  async set(key, value) {
    try {
      getBrowserStorage().setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn('persistent-store localStorage set failed', error);
    }
  },

  async remove(key) {
    try {
      getBrowserStorage().removeItem(key);
    } catch (error) {
      console.warn('persistent-store localStorage remove failed', error);
    }
  },
};

/**
 * xumm.userstore keys: lowercase a-z0-9, min 3 chars (no _ or -).
 * Normalize app keys (e.g. rdt_cid, cosmos-account) so callers stay unchanged.
 */
export const toUserstoreKey = (key) => {
  const normalized = String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (normalized.length < 3) {
    throw new Error(`Invalid userstore key after normalize: ${key}`);
  }
  return normalized;
};

/**
 * xumm.userstore only accepts objects (AnyJson). Wrap arbitrary values so we can
 * persist strings/arrays the same way as localStorage JSON.
 * JSON round-trip so MobX observables become plain data.
 */
const wrapForUserstore = (value) => ({ data: JSON.parse(JSON.stringify(value)) });

const isEmptyPlainObject = (value) =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length === 0;

const unwrapFromUserstore = (stored) => {
  // Xumm returns {} when a key is missing — treat as null, not a real value.
  if (stored == null || isEmptyPlainObject(stored)) return null;
  if (typeof stored !== 'object') return null;
  if ('data' in stored) return stored.data;
  return stored;
};

export const createXummUserstoreBackend = (userstore) => ({
  async get(key) {
    try {
      const stored = await userstore.get(toUserstoreKey(key));
      return unwrapFromUserstore(stored);
    } catch (error) {
      console.warn('persistent-store userstore get failed', error);
      return null;
    }
  },

  async set(key, value) {
    try {
      await userstore.set(toUserstoreKey(key), wrapForUserstore(value));
    } catch (error) {
      console.warn('persistent-store userstore set failed', error);
    }
  },

  async remove(key) {
    try {
      await userstore.delete(toUserstoreKey(key));
    } catch (error) {
      console.warn('persistent-store userstore remove failed', error);
    }
  },
});

let cachedStore = null;
let cachedStorePromise = null;

/**
 * Returns the active persistence backend.
 * xApps → xumm.userstore (after Xumm init); everything else → localStorage.
 */
export async function getPersistentStore() {
  if (cachedStore) return cachedStore;
  if (cachedStorePromise) return cachedStorePromise;

  cachedStorePromise = (async () => {
    if (typeof window !== 'undefined' && isXamanXappEnvironment()) {
      try {
        // Loaded on demand so the xumm/XRPL graph stays out of the shared bundle.
        const { ensureXamanXappInitialized, getXamanXappSnapshot } = await import(
          '@/xrpl/xaman-xapp'
        );
        await ensureXamanXappInitialized();
        const { xumm } = getXamanXappSnapshot();
        if (xumm?.userstore) {
          cachedStore = createXummUserstoreBackend(xumm.userstore);
          return cachedStore;
        }
      } catch (error) {
        console.warn('persistent-store: falling back to localStorage', error);
      }
    }

    cachedStore = localStorageBackend;
    return cachedStore;
  })();

  try {
    return await cachedStorePromise;
  } finally {
    cachedStorePromise = null;
  }
}

/** Test helper — clears the memoized backend selection. */
export function resetPersistentStoreCache() {
  cachedStore = null;
  cachedStorePromise = null;
}
